import { useEffect, useState } from "react";
import { Copy, Download, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Textarea } from "../../ui/textarea";
import { api, formatApiError } from "../../../lib/api";
import { NETWORKS, absolute, buildCaption, shareLink, shareTo } from "../../../lib/share";
import { MaterialThumb } from "./MaterialGrid";

const RESULT = {
  native: "Listo: elige la app en el menú de tu celular. El texto va incluido.",
  whatsapp: "WhatsApp se abrió con tu texto y enlace. Si quieres adjuntar el archivo, descárgalo y envíalo en el mismo chat.",
  facebook: "Facebook se abrió con tu enlace. Pega el texto (ya está copiado) y publica.",
  download: "Archivo descargado y texto copiado. Súbelo en la app y pega el texto.",
  cancelled: "Compartir cancelado.",
};

/** Paso 3 · compartir con un toque en la red elegida. */
export const ShareSheet = ({ material, distId, shareBase, post, onClose, onShared }) => {
  const [network, setNetwork] = useState(post?.network || "whatsapp");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const link = material ? shareLink(shareBase, material.slug, distId, network) : "";
  useEffect(() => {
    if (material) setCaption(buildCaption(material, network, shareLink(shareBase, material.slug, distId, network)));
  }, [material, network, shareBase, distId]);
  if (!material) return null;
  const net = NETWORKS.find((n) => n.id === network);

  const go = async () => {
    setBusy(true);
    try {
      const res = await shareTo(network, material, caption, link);
      if (res.method !== "cancelled") {
        const { data } = await api.post("/me/marketing/share", { slug: material.slug, network, post_id: post?.id || "" });
        onShared(data);
      }
      toast[res.method === "cancelled" ? "message" : "success"](RESULT[res.method]);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const copyText = async () => {
    await navigator.clipboard.writeText(caption);
    toast.success("Texto copiado");
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto rounded-[1.5rem] p-0" data-testid="share-sheet">
        <div className="grid md:grid-cols-[280px,1fr]">
          <div className="relative aspect-square overflow-hidden bg-ink md:aspect-auto md:min-h-full"><MaterialThumb item={material} /></div>
          <div className="p-6">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">{material.title}</DialogTitle>
              <DialogDescription>Paso 3 · Elige la red y comparte con un toque. Tu enlace incluye tu ID {distId}, así cada registro queda en tu red.</DialogDescription>
            </DialogHeader>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="share-networks">
              {NETWORKS.filter((n) => !(n.id === "youtube" && material.type !== "video")).map(({ id, label, Icon, color, text }) => (
                <button key={id} type="button" onClick={() => setNetwork(id)} className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-bold transition-transform duration-200 ${network === id ? "scale-105 ring-2 ring-ink/30" : "opacity-80 hover:opacity-100"}`} style={{ background: color, color: text }} data-testid={`share-net-${id}`}>
                  <Icon className="h-3.5 w-3.5" /> {label}
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-ink/60" data-testid="share-hint">{net.hint}</p>
            <label className="mt-4 block text-xs font-semibold">Texto para {net.label} (puedes editarlo)</label>
            <Textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={6} className="mt-1 bg-paper text-sm" data-testid="share-caption" />
            <p className="mt-2 break-all font-mono text-[11px] text-ink/60" data-testid="share-link">{link}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" onClick={go} disabled={busy} className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-bold disabled:opacity-60" style={{ background: net.color, color: net.text }} data-testid="share-go">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <net.Icon className="h-4 w-4" />} Compartir en {net.label}
              </button>
              <button type="button" onClick={copyText} className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-4 py-3 text-xs font-semibold hover:bg-paper" data-testid="share-copy"><Copy className="h-4 w-4" /> Copiar texto</button>
              <a href={absolute(material.url)} download target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-4 py-3 text-xs font-semibold hover:bg-paper" data-testid="share-download"><Download className="h-4 w-4" /> Descargar</a>
              <button type="button" onClick={onClose} className="ml-auto rounded-full p-3 text-ink/50 hover:bg-paper" aria-label="Cerrar" data-testid="share-close"><X className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
